import React, { useState, useEffect } from 'react';
import { Award, Timer, Star, Crown, Brain } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Language } from '../types';

interface BadgesProps {
  language: Language;
  isOpen: boolean;
  onClose: () => void;
  onBadgesLoaded?: (count: number) => void;
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  earned?: boolean;
  earned_at?: string;
}

const Badges: React.FC<BadgesProps> = ({ language, isOpen, onClose, onBadgesLoaded }) => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadBadges();
    }
  }, [isOpen]);

  const loadBadges = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get all badges and user's earned badges
      const { data: allBadges } = await supabase
        .from('badges')
        .select('*')
        .order('created_at');

      const { data: userBadges } = await supabase
        .from('user_badges')
        .select('badge_id, earned_at')
        .eq('user_id', user.id);

      // Combine the data
      const badgesWithEarned = allBadges?.map(badge => ({
        ...badge,
        earned: userBadges?.some(ub => ub.badge_id === badge.id),
        earned_at: userBadges?.find(ub => ub.badge_id === badge.id)?.earned_at
      })) || [];

      setBadges(badgesWithEarned);
      
      // Notify parent component about the number of earned badges
      const earnedCount = userBadges?.length || 0;
      onBadgesLoaded?.(earnedCount);
    } catch (error) {
      console.error('Error loading badges:', error);
      setBadges([]);
      onBadgesLoaded?.(0);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Award':
        return <Award className="h-8 w-8" />;
      case 'Timer':
        return <Timer className="h-8 w-8" />;
      case 'Star':
        return <Star className="h-8 w-8" />;
      case 'Crown':
        return <Crown className="h-8 w-8" />;
      case 'Brain':
        return <Brain className="h-8 w-8" />;
      default:
        return <Award className="h-8 w-8" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              {language === 'en' ? 'Your Badges' : 'Vos Badges'}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-indigo-600 dark:border-indigo-400 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'en' ? 'Loading badges...' : 'Chargement des badges...'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {badges.map((badge) => (
                <div
                  key={badge.id}
                  className={`p-4 rounded-lg border-2 ${
                    badge.earned
                      ? 'border-indigo-500 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`${
                      badge.earned ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'
                    }`}>
                      {getIcon(badge.icon)}
                    </div>
                    <div>
                      <h3 className={`font-semibold ${
                        badge.earned ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-600 dark:text-slate-400'
                      }`}>
                        {badge.name}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {badge.description}
                      </p>
                      {badge.earned && badge.earned_at && (
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                          {language === 'en' ? 'Earned on ' : 'Obtenu le '}
                          {new Date(badge.earned_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Badges;